import {
	EDocumentType,
	EDocumentStatus,
	EGender,
	EPeriodType,
	ECargo,
	NOMBRE_CARGO,
	EEntidadFederativa,
	NOMBRE_ENTIDAD_FEDERATIVA,
	EStatusNotification,
	EUserRole,
	EPlan,
	ETipoMantenimientoType,
	EStatusFactura
} from './enums'
export interface IBaseDocument {
	id: string
	type: EDocumentType
	status: EDocumentStatus
	folioControl: string
	cadenaOriginal?: string
	xml?: string
	date: number
	package: number
	success: boolean
	warning?: IError
	error?: IError
}

/**
 * @description Interfaz para representar a un estudiante
 * @interface IStudent
 * @property {string} curp - El CURP del estudiante
 * @property {string} nombre - El nombre del estudiante
 * @property {string} primerApellido - El primer apellido del estudiante
 * @property {string} segundoApellido - El segundo apellido del estudiante
 * @property {string} fechaNacimiento - La fecha de nacimiento del estudiante
 */
export interface IStudent {
	curp: string
	nombre: string
	primerApellido: string
	segundoApellido: string
	fechaNacimiento: string
	idGenero: EGender
}
/**
 * @description Interfaz para representar a una institución
 * @interface IInstitution
 * @property {string} idCampus - El ID del campus
 * @property {string} idEntidadFederativa - El ID de la entidad federativa
 * @property {number} idNombreInstitucion - El ID del nombre de la institución
 * @property {string} nombreInstitucion - El nombre de la institución
 * @property {string} campus - El campus de la institución
 */
export interface IInstitution {
	idCampus: string
	idEntidadFederativa: string
	idNombreInstitucion: number
	nombreInstitucion: string
	campus: string
	entidadFederativa: string
}

/**
 * @description Interfaz para representar a un responsable
 * @interface IResponsible
 * @property {string} abrTitulo - El abreviatura del título
 * @property {string} curp - El CURP del responsable
 * @property {string} nombre - El nombre del responsable
 * @property {string} primerApellido - El primer apellido del responsable
 * @property {string} segundoApellido - El segundo apellido del responsable
 * @property {number} idCargo - El ID del cargo
 * @property {string} cargo - El nombre del cargo
 * @property {string} certificadoResponsable - El certificado del responsable
 * @property {string} noCertificadoResponsable - El número de certificado del responsable
 * @property {string} sello - El sello del responsable
 */
export interface IResponsible {
	abrTitulo: string
	curp: string
	nombre: string
	primerApellido: string
	segundoApellido: string
	idCargo: ECargo
	cargo: (typeof NOMBRE_CARGO)[ECargo]
	certificadoResponsable: string
	noCertificadoResponsable: string
	sello: string
}

/**
 * Interfaz para representar a un RVOE
 * @interface IRvoe
 * @property {string} numero - El número del RVOE
 * @property {string} fechaExpedicion - La fecha de expedición del RVOE
 */

export interface IRvoe {
	numero: string
	fechaExpedicion: string
}

export interface ICareer {
	idCarrera: string
	nombreCarrera: string
	cveCarrera: string
	clavePlan: string
	idNivelEstudios: number
	idTipoPeriodo: EPeriodType
	calificacionMaxima: number
	calificacionMinima: number
	calificacionMinimaAprobatoria: string
	tipoPeriodo: keyof typeof EPeriodType
	conectorCarreraNivel?: string
}
export interface ICareerResponse {
	Revoes: { [key: string]: Revoe }
	claveCarrera: string
	idCarrera: string
	idNivelEstudios: number
	nombreCarrera: string
	conectorCarreraNivel?: string
}

export interface Revoe {
	Planes: { [key: string]: Plane }
	calificacionMaxima: number
	calificacionMinima: number
	calificacionMinimaAprobatoria: string
	fechaExpedicion: string
	idAutorizacionReconocimiento: number
	numero: string
}

export interface Plane {
	Asignaturas: { [key: string]: Asignatura }
	campus: (boolean | null)[]
	clavePlan: string
	tipoCarrera: string
	totalAsignaturas: number
	totalCreditos: string
	decimalsForCredits?: number
}

export interface Asignatura {
	claveAsignatura: string
	creditos: string
	idAsignatura: number
	idTipoAsignatura: number
	nombre: string
	order?: number
}

export const EIdNivelEstudios = {
	'81': 'LICENCIATURA',
	'82': 'MAESTRIA',
	'83': 'PROFESIONAL ASOCIADO',
	'84': 'TECNICO SUPERIOR UNIVERSITARIO',
	'85': 'ESPECIALIDAD',
	'95': 'DOCTORADO'
} as const

export type EIdNivelEstudiosKey = keyof typeof EIdNivelEstudios
// type EIdNivelEstudiosKey = "81" | "82" | "83" | "84" | "85" | "95"

export type EIdNivelEstudiosValue = (typeof EIdNivelEstudios)[EIdNivelEstudiosKey]
// type EIdNivelEstudiosValue = "LICENCIATURA" | "MAESTRIA" | ...

export const CIdNivelEstudios: { [key in EIdNivelEstudiosValue]: string } = {
	'LICENCIATURA': 'Licenciatura',
	'MAESTRIA': 'Maestría',
	'PROFESIONAL ASOCIADO': 'Profesional Asociado',
	'TECNICO SUPERIOR UNIVERSITARIO': 'Técnico Superior Universitario',
	'ESPECIALIDAD': 'Especialidad',
	'DOCTORADO': 'Doctorado'
}
export interface ISelectedInstitution {
	CCT: string
	hasCertisep: boolean
	Carrera: ICareerResponse[]
	Campus: ICampus[]
	idNombreInstitucion: number
	cveInstitution: string
	Responsable: any
}
export interface ICampus {
	campus: string
	entidadFederativa: (typeof NOMBRE_ENTIDAD_FEDERATIVA)[EEntidadFederativa]
	idCampus: string
	claveCampus: string
	idEntidadFederativa: EEntidadFederativa
	idNombreInstitucion: number
	nombreInstitucion: string
}
export interface IError {
	message: string
	code: string
	error: {
		message: string
		code: string
		success: boolean
		value: boolean
	}
	time: number
}
export interface IUserRole {
	tipoRole: EUserRole
	descriptionRole: string
}
export const ROLE_IDS = {
	ADMIN_OWNER: 1,
	ADMINISTRADOR: 2,
	VISOR: 3,
	USUARIO_EXTERNO_TEMPORAL: 4
} as const

export type RoleId = (typeof ROLE_IDS)[keyof typeof ROLE_IDS]
export interface IRole {
	id: number
	roleName: string
	roleDescription: string | null
	internalRole: boolean
}
export const SEED_ROLES: readonly IRole[] = [
	{
		id: ROLE_IDS.ADMIN_OWNER,
		roleName: 'Administrador Owner',
		roleDescription: 'Control total de la institución; rol interno, asignación solo explícita.',
		internalRole: true
	},
	{
		id: ROLE_IDS.ADMINISTRADOR,
		roleName: 'Administrador',
		roleDescription: 'Gestión operativa dentro del alcance institucional asignado.',
		internalRole: false
	},
	{
		id: ROLE_IDS.VISOR,
		roleName: 'Visor',
		roleDescription: 'Consulta de información sin modificación.',
		internalRole: false
	},
	{
		id: ROLE_IDS.USUARIO_EXTERNO_TEMPORAL,
		roleName: 'Usuario Externo Temporal',
		roleDescription: 'Acceso por invitación o token; alcance limitado y temporal.',
		internalRole: false
	}
]

/** Metadatos MFA TOTP propio de Alumni (RTDB `users/{uid}/userData/mfa`). Secreto cifrado en Data Connect `AlumniUserMfaVault`. */
export interface IAlumniMfaRtdb {
	enrolled?: boolean
	enrolledAt?: string
	/** @deprecated SM por usuario; migración a vault DC. */
	secretId?: string
	pending?: boolean
	pendingAt?: string
	pendingRevision?: number
}

export interface IUser {
	cer: Cer
	role?: EUserRole
	alumniRole?: IRole
	curp: string
	email: string
	name: string
	genere: EGender
	phone: string
	userData: {
		activatedDevice: string
		institution: number
		testMode: boolean
		base64Cer: string
		userDevices: Record<string, IUserDevice>
		mfa?: IAlumniMfaRtdb
	}
}
export interface IUserDevice {
	TOCAccepted: boolean
	banned: boolean
	biometricProfile: boolean
	device: IDevice
	faces: Record<string, boolean>
	pushToke: string
	uuid: string
}
export interface IDevice {
	android: boolean
	ios: boolean
	deviceInfo: IDeviceInfo
	screenInfo: IScreenInfo
	sign: boolean
}
export interface IDeviceInfo {
	deviceType: string
	language: string
	manufacturer: string
	model: string
	os: string
	osVersion: string
	region: string
	sdkVersion: string
}
export interface IScreenInfo {
	height: number
	width: number
	scale: number
}
export interface IExtendedUser extends IUser {
	institutions: {
		[key: string]: string
	}
}

export interface Cer {
	certificadoResponsable: string
	noCertificadoResponsable: string
	pem: string
	x509: X509
}

export interface X509 {
	extensions: Extensions
	fingerPrint: string
	issuer: Issuer[]
	notAfter: Date
	notBefore: Date
	publicKey: PublicKey
	serial: string
	siginfo: Siginfo
	signature: string
	subject: Subject
	subjectHash: string
	version: number
}

export interface Extensions {
	basicConstraints: BasicConstraints
	extKeyUsage: EXTKeyUsage
	keyUsage: KeyUsage
	nsCertType: NSCERTType
}

export interface BasicConstraints {
	cA: boolean
	critical: boolean
	id: string
	name: string
	value: string
}

export interface EXTKeyUsage {
	clientAuth: boolean
	critical: boolean
	emailProtection: boolean
	id: string
	name: string
	value: string
}

export interface KeyUsage {
	cRLSign: boolean
	critical: boolean
	dataEncipherment: boolean
	decipherOnly: boolean
	digitalSignature: boolean
	encipherOnly: boolean
	id: string
	keyAgreement: boolean
	keyCertSign: boolean
	keyEncipherment: boolean
	name: string
	nonRepudiation: boolean
	value: string
}

export interface NSCERTType {
	client: boolean
	critical: boolean
	email: boolean
	emailCA: boolean
	id: string
	name: string
	objCA: boolean
	objsign: boolean
	reserved: boolean
	server: boolean
	sslCA: boolean
	value: string
}

export interface Issuer {
	name?: string
	shortName?: string
	type: string
	value: string
	valueTagClass: number
}

export interface PublicKey {
	bitSize: number
	fingerPrint: string
	pem: string
}

export interface Siginfo {
	algorithmOid: string
}

export interface Subject {
	'2-5-4-41': string
	'2-5-4-45': string
	commonName: string
	countryName: string
	emailAddress: string
	organizationName: string
	serialNumber: string
}

export interface IInstitutionConfigurationTestModeDegreeServices {
	id: string
	url: string
	user: string
}
export interface IInstitutionConfiguration {
	certificates: {
		decimals: number
		cycleMask: string
		hasAccents: boolean
		mayus: boolean
	}
	degree: {
		conectorCarreraNivel: string
		hasAccents: boolean
		services: IInstitutionConfigurationTestModeDegreeServices
	}
}
export interface INotification {
	id: string
	title: string
	text: string
	time: string
	status: EStatusNotification
}
export interface IExtendedInstitution extends IInstitution {
	idInstitution: string
}
/**
 * Interfaz para la bitácora de una institución
 */
export interface BitacoraData {
	idInstitution: string
	institutionName: string
	totalDocumentsProduction: number
	totalDocumentsTest: number
	modificationDate: number
	LastUserWhomodified: string
	pricePerDocument: number
	fechaInicioContrato: number
	fechaFinContrato: number
	precioPlan: number
	limitDocumentsPostpaid?: number
	iva: number
	plan: EPlan
}

/**
 * Interfaz para las transacciones de una institución
 */
export interface TransactionData {
	idInstitution: string
	userId: string //Usuario que realizó la transacción
	typeTransaction: 'decremento' | 'aumento' //Tipo de transacción
	quantityDocuments?: number //Cantidad de documentos que se firmaron
	previousBalance: number //Balance anterior
	currentBalance: number //Balance actual
	dateModification: number //Fecha de modificación
	pricePerDocument?: number //Precio por documento
	details: string //Detalles de la transacción
	packageId?: string //ID del paquete
	environment: 'production' | 'test' //Ambiente de la transacción
}
export interface IResponsable {
	cer: {
		certificadoResponsable: string
	}
	certificates?: {
		cargo: string
		idCargo: number | null
	}
	degrees?: {
		cargo: string
		idCargo: number | null
		abrTitulo: string | null
	}
	nombre: string
	primerApellido: string
	segundoApellido: string
	phone: string
}
export interface IMantenimiento {
	mensaje: string
	titulo: string
	tipo: ETipoMantenimientoType
	fechaInicio?: number
	fechaFin?: number
}
export interface INotificationPayment {
	description: string
	proof: string
	amount: number
	idInstitution: string
	institutionName: string
	dateToSendNotification: number
}
export interface IFactura {
	title: string
	idInstitution: string
	institutionName: string
	dateFactura: number
	status: EStatusFactura
	pdfPath: string
	xmlPath: string
	complementPath?: string
}
export interface IPUIHistory {
	idInstitution: string
	date: number
	user: string
	studentsCount: number
	excelPath: string
	txtPath: string
	fileNames: string[]
}
